import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, ShieldCheck } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { useToast } from '../../../contexts/ToastContext';
import {
  obtenerAplicaciones, crearAplicacion, actualizarAplicacion, guardarPermisos,
  type Aplicacion, type AplicacionesResp,
} from '../../../services/correoService';
import { mensajeError } from './errores';
import styles from './CorreoModule.module.css';

/** RF-05 — Aplicaciones consumidoras, permisos y límites. */
export default function AplicacionesCorreoPage() {
  const { addToast } = useToast();
  const [datos, setDatos] = useState<AplicacionesResp | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [nueva, setNueva] = useState({ clientId: '', nombre: '', nombreRemitente: '', limitePorMinuto: 60 });
  const [editaPermisos, setEditaPermisos] = useState<Aplicacion | null>(null);
  const [permisosSel, setPermisosSel] = useState<Set<string>>(new Set());

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setDatos(await obtenerAplicaciones());
    } catch (err) {
      addToast('error', mensajeError(err));
    } finally {
      setCargando(false);
    }
  }, [addToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const crear = async () => {
    setGuardando(true);
    try {
      await crearAplicacion(nueva);
      addToast('success', `Aplicación ${nueva.nombre} creada.`);
      setNueva({ clientId: '', nombre: '', nombreRemitente: '', limitePorMinuto: 60 });
      await cargar();
    } catch (err) {
      addToast('error', mensajeError(err));
    } finally {
      setGuardando(false);
    }
  };

  const alternarActiva = async (a: Aplicacion) => {
    try {
      await actualizarAplicacion({ id: a.aplicacionId, activa: !a.activa });
      await cargar();
    } catch (err) {
      addToast('error', mensajeError(err));
    }
  };

  const abrirPermisos = (a: Aplicacion) => {
    setEditaPermisos(a);
    setPermisosSel(new Set(a.permisos));
  };

  const togglePermiso = (codigo: string) => {
    setPermisosSel((prev) => {
      const s = new Set(prev);
      if (s.has(codigo)) s.delete(codigo); else s.add(codigo);
      return s;
    });
  };

  const guardarPerm = async () => {
    if (!editaPermisos) return;
    setGuardando(true);
    try {
      await guardarPermisos({ id: editaPermisos.aplicacionId, tiposCorreo: [...permisosSel] });
      addToast('success', 'Permisos actualizados.');
      setEditaPermisos(null);
      await cargar();
    } catch (err) {
      addToast('error', mensajeError(err));
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) return <div className={styles.vacio}><Loader2 className="spinner" size={28} /></div>;

  return (
    <div>
      <div className={styles.form}>
        <div className={styles.campo}>
          <label htmlFor="a-client">ClientId (B2C)</label>
          <input id="a-client" value={nueva.clientId}
            onChange={(e) => setNueva((n) => ({ ...n, clientId: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="a-nombre">Nombre</label>
          <input id="a-nombre" value={nueva.nombre}
            onChange={(e) => setNueva((n) => ({ ...n, nombre: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="a-remite">Nombre del remitente</label>
          <input id="a-remite" value={nueva.nombreRemitente}
            onChange={(e) => setNueva((n) => ({ ...n, nombreRemitente: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="a-limite">Límite por minuto</label>
          <input id="a-limite" type="number" min={1} value={nueva.limitePorMinuto}
            onChange={(e) => setNueva((n) => ({ ...n, limitePorMinuto: Number(e.target.value) }))} />
        </div>
        <Button size="sm" loading={guardando} onClick={crear}
          disabled={!nueva.clientId || !nueva.nombre || !nueva.nombreRemitente}>
          <Plus size={15} /> Alta de aplicación
        </Button>
      </div>

      <div className={styles.tablaScroll}>
        <table className={styles.tabla}>
          <thead>
            <tr>
              <th>Nombre</th><th>ClientId</th><th>Remitente</th><th>Límite/min</th>
              <th>Permisos</th><th>Estado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {(datos?.aplicaciones ?? []).map((a) => (
              <tr key={a.aplicacionId}>
                <td>{a.nombre}{a.esAdmin && <> <span className={styles.chip}>admin</span></>}</td>
                <td>{a.clientId}</td>
                <td>{a.nombreRemitente}</td>
                <td>{a.limitePorMinuto}</td>
                <td>{a.permisos.length === 0 ? <span className={styles.kpiEtiqueta}>—</span>
                  : a.permisos.map((p) => <span key={p} className={styles.chip} style={{ marginRight: 4 }}>{p}</span>)}</td>
                <td><span className={`${styles.chip} ${a.activa ? styles.chipOk : styles.chipErr}`}>{a.activa ? 'Activa' : 'Inactiva'}</span></td>
                <td>
                  <div className={styles.acciones}>
                    <Button variant="ghost" size="sm" onClick={() => abrirPermisos(a)}>
                      <ShieldCheck size={15} /> Permisos
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => alternarActiva(a)}>
                      {a.activa ? 'Desactivar' : 'Activar'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={editaPermisos !== null} onClose={() => setEditaPermisos(null)}
        title={editaPermisos ? `Permisos de ${editaPermisos.nombre}` : 'Permisos'}>
        <p className={styles.kpiEtiqueta}>Tipos de correo que esta aplicación puede enviar:</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', margin: '0.75rem 0' }}>
          {(datos?.tipos ?? []).map((t) => (
            <label key={t.tipoCorreoId} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--font-size-sm)' }}>
              <input type="checkbox" checked={permisosSel.has(t.codigo)} onChange={() => togglePermiso(t.codigo)} />
              {t.codigo}
            </label>
          ))}
        </div>
        <div className={styles.acciones} style={{ justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" onClick={() => setEditaPermisos(null)}>Cancelar</Button>
          <Button size="sm" loading={guardando} onClick={guardarPerm}>Guardar permisos</Button>
        </div>
      </Modal>
    </div>
  );
}
